import { Button } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { LeadWizardCustomerRow } from "./LeadWizardCustomerRow.tsx"
import { LeadWizardMatchPanel } from "./LeadWizardMatchPanel.tsx"
import {
  type CustomerMatchCandidate,
  MAX_VISIBLE_MATCHES,
  candidateKey
} from "./customer-match.ts"

interface LeadWizardDuplicateWarningProps {
  matches: CustomerMatchCandidate[]
  onUseExisting: (candidate: CustomerMatchCandidate) => void
  onCreateNew: () => void
  busyKey?: string | null
}

export function LeadWizardDuplicateWarning({
  matches,
  onUseExisting,
  onCreateNew,
  busyKey
}: LeadWizardDuplicateWarningProps) {
  const { t } = useTranslation()

  if (!matches.length) return null

  // Ranked strongest-first, so the tail is the part worth dropping. The lookup
  // asks for one row past this, so a list longer than it means more exist.
  const visible = matches.slice(0, MAX_VISIBLE_MATCHES)
  const hasMore = matches.length > visible.length

  return (
    <LeadWizardMatchPanel
      title={t("leadWizard.duplicate.title")}
      description={t("leadWizard.duplicate.description")}
      more={hasMore ? t("leadWizard.duplicate.more") : undefined}
      footer={
        <Button size={"small"} type={"link"} onClick={onCreateNew}>
          {t("leadWizard.duplicate.createNew")}
        </Button>
      }
    >
      {visible.map((candidate) => (
        <LeadWizardCustomerRow
          key={candidateKey(candidate)}
          withReason
          candidate={candidate}
          busy={busyKey === candidateKey(candidate)}
          actionLabel={t("leadWizard.duplicate.useExisting")}
          onUse={() => onUseExisting(candidate)}
        />
      ))}
    </LeadWizardMatchPanel>
  )
}
